import React from "react";
import { DocumentEditor } from "@onlyoffice/document-editor-react";
import { useTranslation } from "react-i18next";

import { config } from "@root/server/config";
import {
  useAuthentication,
  useDocumentEditionTokenRetrieval,
  useRetrieveDocumentKey,
} from "@planzone/view-models";
import { EditDocument as D } from "@planzone/dto";
import { Window } from "@common/window";
import { Loader } from "@common/loader";
import { Button } from "@common/button";
import { getDocumentType, getFileType } from "@components/checkFileType";
import { Props } from "./types";
import styles from "./styles.scss";

function Edit({ document, close }: Props) {
  const [generatedToken, setGeneratedToken] = React.useState<string>();
  const { t } = useTranslation();
  const { account } = useAuthentication();
  const { key, retrieveDocumentKey, isRequestSuccess, isRequestFailure } =
    useRetrieveDocumentKey(document.uuid);
  const documentData: D = {
    fileType: getFileType(document.mimeType),
    key: `${document.uuid}-${key}`,
    title: document.name,
    url: `${config.clientUrl}/proxy?url=${document.uri}&access_token=${account.token}`,
    referenceData: {
      fileKey: document.uuid,
      key: document.uuid,
      instanceId: document.uri,
    },
  };
  const {
    retrieveDocumentEditionToken,
    isRequestSuccess: isRetrieveOnlyOfficeRequestSuccess,
    token,
  } = useDocumentEditionTokenRetrieval(document.uuid);
  // @ts-ignore
  const callBackUrl = `${config.clientUrl}/callback?access_token=${account.token}&file_id=${document.uuid}&parent_id=${document.parentUuid}&type_id=${document.projectUuid}&filename=${document.name}&type=project`;

  React.useEffect(() => {
    retrieveDocumentKey({ documentId: document.uuid });
  }, [document]);

  React.useEffect(() => {
    isRequestFailure && close();
  }, [isRequestFailure]);

  React.useEffect(() => {
    isRequestSuccess &&
      retrieveDocumentEditionToken({
        callbackUrl: callBackUrl,
        documentData,
        userid: account.uuid,
        username: `${account.firstName} ${account.lastName}`,
      });
  }, [key, isRequestSuccess]);

  React.useEffect(() => {
    isRetrieveOnlyOfficeRequestSuccess && setGeneratedToken(token);
  }, [isRetrieveOnlyOfficeRequestSuccess]);

  return (
    <Window close={close}>
      <div className={styles.preview}>
        {generatedToken ? (
          <DocumentEditor
            id="docxEditor"
            documentServerUrl={config.onlyOfficeUrl}
            config={{
              document: documentData,
              editorConfig: {
                callbackUrl: callBackUrl,
                customization: {
                  autosave: true,
                  forcesave: true,
                },
                user: {
                  id: account.uuid,
                  name: `${account.firstName} ${account.lastName}`,
                },
              },
              token: generatedToken,
              documentType: getDocumentType(document.mimeType),
            }}
          />
        ) : (
          <div className={styles.loader}>
            <div>
              <Loader />
            </div>
          </div>
        )}
        <div className={styles.footer}>
          <Button className={styles.closeButton} type={"submit"} press={close}>
            {t("documentEdition.close")}
          </Button>
        </div>
      </div>
    </Window>
  );
}

export { Edit };
